import { useCallback, useEffect, useMemo, useState } from 'react';
import tableTemplatesService, { type TableLayoutTemplate } from '../services/tableTemplates.service';
import { prepareTableTemplate } from '../utils/tableTemplateCompatibility';
import { useProSearchDestination, type ProSearchDestination } from './useProSearchDestination';

/* Views the search-results PRO grid can wear.
 *
 * NOT just the two search keys. A layout is a layout: one arranged on an LC bucket
 * card is a perfectly good way to look at search results, and the columns resolve
 * across grids that name a field differently (utils/fieldAliases). So this lists
 * every accessible layout from EVERY grid, and `useCompatibleSearchViews` — what
 * the picker actually renders — keeps the ones that will genuinely load here.
 *
 * Measured on prod before this: one LC user had 22 saved layouts and the picker
 * offered 3, the only three saved under `search-results-pro-table`. The other 19
 * were reachable only by entering the grid's column-edit mode and using Load View.
 *
 * The results grid keeps two independent layouts under two settings keys — table
 * mode and Multi Row (card) mode (SearchResults.tsx) — and choosing a View puts the
 * page into the mode it belongs to.
 *
 * Consumed by the View dropdown in the Custom Search / Custom Filter modals and
 * by SearchResults, which resolves ?view=<id> against the same list rather than
 * fetching a single row (no extra endpoint, and the two can never disagree). */

export const SEARCH_VIEW_TABLE_KEY = 'search-results-pro-table';
export const SEARCH_VIEW_CARDS_KEY = 'search-results-pro-cards';

export type SearchViewMode = 'table' | 'cards';

export interface SearchView {
  id: number;
  name: string;
  /** Which results-page layout this View was saved from. */
  mode: SearchViewMode;
  settingsKey: string;
  settingValue: any;
  publishedBy: string;
  canManage: boolean;
  /**
   * `view`     = is_view=1 — manager-published and org-wide. The UI calls this a
   *              "SYSTEM VIEW" (published from "Save As System View").
   * `template` = is_view=0 — one of the CALLER'S OWN layouts. The UI calls this
   *              plain a "VIEW" ("Save As View" / "Load View"), and offers it in
   *              this picker under "My Views" so applying a personal layout to a
   *              search does not mean entering column edit mode.
   *
   * TERMINOLOGY WARNING: the UI vocabulary and the code vocabulary are offset by
   * one. `is_view` / `kind: 'view'` is the SYSTEM View; a `template` is what users
   * now simply call a View. The DB column, the endpoints and the
   * `save-search-view` component id keep the original names deliberately —
   * renaming a seeded component id would orphan the grant already in prod.
   *
   * Both kinds are rows in table_layout_templates, so `id` is unique across them
   * and one `?view=<id>` param resolves either — no separate URL scheme.
   */
  kind: 'view' | 'template';
}

/**
 * Which results-page mode a layout belongs in.
 *
 * For the two search keys the KEY decides, as it always has — that is what the
 * layout was saved against. A layout from any other grid has no such key, so it
 * falls back to the `layoutMode` buildTemplateSnapshot stamps on it, and finally to
 * table: the Single Row grid is the one that can show an arbitrary column set.
 */
const modeOf = (row: TableLayoutTemplate): SearchViewMode => {
  if (row.settings_key === SEARCH_VIEW_CARDS_KEY) return 'cards';
  if (row.settings_key === SEARCH_VIEW_TABLE_KEY) return 'table';
  return (row.setting_value as { layoutMode?: unknown } | null)?.layoutMode === 'cards'
    ? 'cards'
    : 'table';
};

const toSearchView = (row: TableLayoutTemplate): SearchView => ({
  id: row.id,
  name: row.name,
  mode: modeOf(row),
  settingsKey: row.settings_key,
  settingValue: row.setting_value,
  publishedBy: row.user_email,
  canManage: !!row.can_manage,
  kind: row.is_view ? 'view' : 'template',
});

export const MODE_LABEL: Record<SearchViewMode, string> = {
  table: 'Table',
  cards: 'Multi Row',
};

export interface UseSearchViewsResult {
  /** Manager-published, org-wide Views. */
  views: SearchView[];
  /** The caller's OWN layout templates — never anyone else's shared ones. */
  myViews: SearchView[];
  /** Both, for resolving a `?view=<id>` that could be either kind. */
  all: SearchView[];
  /** True until the first fetch settles — consumers gate on this so a View in the URL is never briefly missing. */
  loading: boolean;
  /** The fetch failed. Views are treated as "none" rather than blocking the search. */
  failed: boolean;
  reload: () => void;
}

/**
 * Fetch every layout the search-results grid could wear: the published System
 * Views, plus the caller's own templates, from every grid in the app.
 *
 * This is the RAW list — it includes layouts whose columns this grid cannot show.
 * The picker uses useCompatibleSearchViews; `?view=<id>` resolves against this one
 * and lets resolveActiveView judge the single row it was handed, so a stale link to
 * a View that no longer fits reports itself rather than silently going missing.
 *
 * Other people's SHARED templates are deliberately dropped. "My Views" means the
 * caller's own; a colleague's shared layout is reachable from "Load View",
 * where it is labelled as theirs. Mixing them into this picker would make someone
 * else's personal layout look like an official View.
 *
 * A failed fetch resolves to empty lists, never a thrown error: this is an
 * enhancement to searching, so losing it must not stop a search from running.
 * `failed` is exposed so a caller can tell "nothing to offer" (hide the dropdown)
 * apart from "we could not look" (say so).
 */
export function useSearchViews(enabled: boolean = true): UseSearchViewsResult {
  const [rows, setRows] = useState<TableLayoutTemplate[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    // Two requests, across every settings key, and the listings are disjoint
    // (?views=1 returns only System Views, the default returns only templates), so
    // nothing arrives twice. The key in the path is ignored in all-keys mode — it
    // only fills the route segment.
    Promise.all([
      tableTemplatesService.listAllViews(),
      tableTemplatesService.list(SEARCH_VIEW_TABLE_KEY, true),
    ])
      .then(([published, templates]) => {
        if (cancelled) return;
        // list() serves own + others' shared; keep only the caller's own.
        setRows([...published, ...templates.filter((r) => r.is_mine)]);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, nonce]);

  // Alphabetical WITHIN each kind, across both modes, so each group reads as one
  // list rather than two concatenated ones. Names are unique per (kind, mode) but
  // not across modes, so ties break on mode to keep the order stable between
  // renders.
  const { views, myViews, all } = useMemo(() => {
    const mapped = rows.map(toSearchView);
    const byName = (a: SearchView, b: SearchView) =>
      a.name.localeCompare(b.name) || a.mode.localeCompare(b.mode);
    const published = mapped.filter((v) => v.kind === 'view').sort(byName);
    const mine = mapped.filter((v) => v.kind === 'template').sort(byName);
    return { views: published, myViews: mine, all: [...published, ...mine] };
  }, [rows]);

  return { views, myViews, all, loading, failed, reload };
}

/**
 * What the View picker renders: the layouts that will genuinely load on this grid.
 *
 * A layout from another grid is offered only if every column it depends on exists
 * here or can be added — the same judgement the grid itself makes on apply
 * (prepareTableTemplate), against the same destination, so the picker can never
 * offer something that is then refused. Layouts built on another board's own
 * columns — a trouble-ticket board's `reason`/`resolve`, the pending-invoicing
 * drill's `invoicecomments` — are the ones this drops, because nothing on the
 * results grid shows that data.
 *
 * Until the field registry has loaded, nothing can be judged and the raw list is
 * passed through unfiltered rather than being falsely emptied.
 */
export function useCompatibleSearchViews(enabled: boolean = true): UseSearchViewsResult {
  const base = useSearchViews(enabled);
  const destination = useProSearchDestination();
  const { views: allViews, myViews: allMine } = base;
  return useMemo(() => {
    if (!destination.ready) return { ...base, all: [...allViews, ...allMine] };
    const fits = (v: SearchView) => !prepareTableTemplate(v.settingValue, destination).reason;
    const views = allViews.filter(fits);
    const myViews = allMine.filter(fits);
    return { ...base, views, myViews, all: [...views, ...myViews] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allViews, allMine, destination, base.loading, base.failed, base.reload]);
}

export interface ResolvedSearchView {
  /** The matched row, or null. Non-null does NOT imply usable — check `active`. */
  view: SearchView | null;
  /** Apply this View: it resolved AND carries a usable snapshot. */
  active: boolean;
  /** A View was asked for but cannot be applied. Worth telling the user. */
  unresolved: boolean;
  /** The layout blob to hand the grid, or undefined when not active. */
  snapshot: any;
  /** The dynamic columns the View was saved with — the host must fetch these. */
  additionalFields: string[];
  /** Which results-page mode to render in: the View's, else the user's choice. */
  mode: SearchViewMode;
}

/**
 * Decide what the results page should do with `?view=<id>`.
 *
 * Split out of SearchResults so the failure modes are testable on their own —
 * they are the whole reason this is not a one-line `find`:
 *
 *   * A link shared last month whose View has since been DELETED resolves to
 *     nothing. Falling through to the grid's hard defaults would look like a
 *     broken page, so the user's own layout is used and `unresolved` says why.
 *   * A row whose snapshot is EMPTY is the endpoint degrading past a corrupt blob
 *     (migration 373's _parse_setting_value serves {} rather than 500ing the whole
 *     list). Applying {} means "defaults", which is the same broken-looking page,
 *     so an empty snapshot is treated as no View at all.
 *   * While the list is still loading, NOTHING is decided: `active` false and
 *     `unresolved` false. Callers gate their first fetch on this, because
 *     resolving late would fire a second search with a different column set.
 */
export function resolveActiveView(
  views: SearchView[],
  viewIdParam: string,
  loading: boolean,
  fallbackMode: SearchViewMode,
  /**
   * Fit the View's layout to this grid — prepareTableTemplate bound to the results
   * destination. A View from another grid names its columns as THAT grid spelled
   * them, and the snapshot is applied by id, so what goes to the table has to be
   * the translated one. A layout that cannot be fitted resolves as `unresolved`,
   * the same as a deleted row: the page says so instead of rendering a layout with
   * half its columns silently missing.
   *
   * Optional, and omitted while the field registry is still loading — then nothing
   * can be judged, and applying the snapshot as saved is what this always did.
   */
  prepare?: (snapshot: any) => { snapshot: any; reason?: string },
): ResolvedSearchView {
  const idle = { view: null, active: false, unresolved: false, snapshot: undefined, additionalFields: [], mode: fallbackMode };

  if (!viewIdParam) return idle;
  if (loading) return idle;

  const view = views.find((v) => String(v.id) === viewIdParam) ?? null;
  const snapshot = view?.settingValue;
  const usable =
    !!view && !!snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)
    && Object.keys(snapshot).length > 0;

  if (!usable) return { ...idle, view, unresolved: true };

  const fitted = prepare ? prepare(snapshot) : { snapshot, reason: undefined };
  if (fitted.reason) return { ...idle, view, unresolved: true };

  // The FITTED snapshot's additionalFields, not the saved ones: fitting resolves
  // which of them this grid already shows as a built-in column and which it must
  // actually fetch, and the host fetches exactly what it is told here.
  const additional = fitted.snapshot?.additionalFields;
  return {
    view,
    active: true,
    unresolved: false,
    snapshot: fitted.snapshot,
    additionalFields: Array.isArray(additional) ? (additional as string[]) : [],
    mode: view!.mode,
  };
}

export default useSearchViews;
